import { isDiscouraged } from "@spectree/shared";
import { AlertTriangle, Cpu } from "lucide-react";
import type { Epic } from "@/lib/api/types";
import { cn } from "@/lib/utils";

interface ModelRecommendationCardProps {
  epic: Epic;
}

function ThinkingBadge({ level }: { level: string }) {
  const colors: Record<string, string> = {
    none: "bg-zinc-700 text-zinc-300",
    low: "bg-blue-900/50 text-blue-300",
    medium: "bg-yellow-900/50 text-yellow-300",
    high: "bg-red-900/50 text-red-300",
  };
  const colorClass = colors[level] ?? "bg-zinc-700 text-zinc-300";
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
        colorClass,
      )}
    >
      {level}
    </span>
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function ModelRecommendationCard({ epic }: ModelRecommendationCardProps) {
  const hasRecommendation = !!epic.recommendedModel;
  const hasActual = !!epic.actualModelUsed;
  const hasModelMismatch =
    hasRecommendation && hasActual && epic.recommendedModel !== epic.actualModelUsed;

  if (!hasRecommendation && !hasActual) return null;

  const recommendedModelName = epic.recommendedModel ?? null;
  const actualModelName = epic.actualModelUsed ?? null;
  const actualModelIsDiscouraged = !!epic.actualModelUsed && isDiscouraged(epic.actualModelUsed);
  const hasRecommendationMeta =
    epic.modelRecommendationSource != null || epic.modelRecommendationUpdatedAt != null;

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center gap-2 text-sm font-medium text-zinc-300">
        <Cpu className="h-4 w-4 text-zinc-400" />
        AI Model Recommendation
      </div>

      {/* Recommended section */}
      {hasRecommendation && (
        <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3 space-y-2">
          <div className="flex items-center gap-2 text-xs font-medium text-zinc-400 uppercase tracking-wide">
            Recommended
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-zinc-200">{recommendedModelName}</span>
            {epic.recommendedThinkingLevel && (
              <ThinkingBadge level={epic.recommendedThinkingLevel} />
            )}
          </div>
          {epic.modelRecommendationReason && (
            <p className="text-xs text-zinc-400 italic">
              {epic.modelRecommendationReason}
            </p>
          )}
          {hasRecommendationMeta && (
            <p className="text-xs text-zinc-500">
              {epic.modelRecommendationSource && (
                <span>Source: {epic.modelRecommendationSource}</span>
              )}
              {epic.modelRecommendationSource && epic.modelRecommendationUpdatedAt && " · "}
              {epic.modelRecommendationUpdatedAt && (
                <span>{formatDate(epic.modelRecommendationUpdatedAt)}</span>
              )}
            </p>
          )}
        </div>
      )}

      {/* Actual section */}
      {hasActual && (
        <div
          className={cn(
            "rounded-lg border border-white/[0.06] bg-white/[0.02] p-3 space-y-2",
            hasModelMismatch && "border-amber-500/40 bg-amber-500/5",
          )}
        >
          <div className="flex items-center gap-2 text-xs font-medium text-zinc-400 uppercase tracking-wide">
            Model Used
            {hasModelMismatch && (
              <span className="inline-flex items-center gap-1 rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium text-amber-300">
                <AlertTriangle className="h-3 w-3" />
                Mismatch
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-zinc-200">{actualModelName}</span>
            {actualModelIsDiscouraged && (
              <span className="inline-flex items-center gap-1 rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium text-amber-300">
                <AlertTriangle className="h-3 w-3" />
                Discouraged
              </span>
            )}
            {epic.actualThinkingLevelUsed && (
              <ThinkingBadge level={epic.actualThinkingLevelUsed} />
            )}
          </div>
          {actualModelIsDiscouraged && (
            <p className="text-xs italic text-amber-300/80">
              This model is discouraged for SpecTree work.
            </p>
          )}
          {hasModelMismatch && (
            <p className="text-xs italic text-zinc-400">
              Actual model usage differs from the recommendation.
            </p>
          )}
          {epic.actualModelRecordedAt && (
            <p className="text-xs text-zinc-500">
              Recorded {formatDate(epic.actualModelRecordedAt)}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
